import { Link } from "react-router-dom";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { ArrowRight, Users, Target, Sparkles, GitBranch, ShieldCheck, Activity, BarChart3, Workflow } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1764869024302-a344a7046be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3Nzc5NTg3NzR8MA&ixlib=rb-4.1.0&q=85";

const AI_ICONS = [Activity, ShieldCheck, BarChart3, Workflow];

export default function HomePage() {
  const content = useSiteContent();
  const home = content?.home || {};
  const models = home.models_compare || [];
  const aiPillars = home.ai_pillars || [];

  return (
    <PublicShell>
      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-dotgrid opacity-70 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-6 sm:px-10 lg:px-16 pt-16 lg:pt-28 pb-12 lg:pb-24">
            <div className="flex items-center gap-3 mb-6 animate-fade-up" style={{ animationDelay: "0.05s" }}>
              <span className="status-dot" />
              <span className="overline">{home.hero_eyebrow || "Technology Delivery Platform"}</span>
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-obsidian animate-fade-up"
              style={{ animationDelay: "0.15s" }}
              data-testid="hero-title"
            >
              {home.hero_title || "Technology Delivery Built for Execution"}
            </h1>
            <p
              className="mt-8 max-w-2xl text-base lg:text-lg text-muted-foreground leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.25s" }}
              data-testid="hero-subtitle"
            >
              {home.hero_subtitle || "Nexra provides experienced delivery capability across program leadership, business analysis, development and testing — combined with AI-enabled operational models to support modern execution."}
            </p>
            <p
              className="mt-4 max-w-2xl text-sm lg:text-base font-medium text-execution animate-fade-up"
              style={{ animationDelay: "0.3s" }}
              data-testid="hero-ai-subline"
            >
              {home.ai_subline || "Experienced people, AI-enabled operations. Built for speed, visibility and outcomes."}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-up" style={{ animationDelay: "0.35s" }}>
              <Link to="/engage" data-testid="hero-cta-primary" className="btn-primary">
                {home.hero_primary_cta || "Engage Nexra"}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/join-network" data-testid="hero-cta-secondary" className="btn-secondary">
                {home.hero_secondary_cta || "Join Nexra Network"}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-[560px] border-l border-border overflow-hidden">
            <img src={HERO_IMG} alt="Architectural execution" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-obsidian/20 to-obsidian/80" />
            <div className="absolute top-6 right-6 chip-dark">
              <span className="h-1.5 w-1.5 bg-execution rounded-full" />
              SYS_01 · Online
            </div>
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/80 mb-2">Operating model</div>
              <div className="font-heading text-lg sm:text-xl tracking-tight">
                People + AI, engineered for delivery outcomes.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      {home.stats && (
        <section className="border-b border-border bg-white" data-testid="stats-section">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3">
            {home.stats.map((s, i) => (
              <div
                key={i}
                className={`px-6 sm:px-10 lg:px-16 py-10 lg:py-12 ${i !== 0 ? "md:border-l border-border" : ""} ${i !== home.stats.length - 1 ? "border-b md:border-b-0 border-border" : ""}`}
              >
                <div className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter text-obsidian">{s.value}</div>
                <div className="mt-3 text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* What We Do */}
      <section className="section" data-testid="what-we-do-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="overline mb-4">01 — What We Do</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {home.what_we_do_title}
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pt-4">
            <p className="text-lg lg:text-xl text-foreground/80 leading-relaxed max-w-2xl">{home.what_we_do_body}</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-border">
              {(content?.services?.items || []).slice(0, 4).map((s, i, arr) => (
                <div
                  key={s.key || i}
                  className={`p-5 lg:p-6 ${i !== 0 ? "lg:border-l border-border" : ""} ${i % 2 === 1 ? "md:border-l border-border" : ""} ${i < arr.length - 2 ? "border-b md:border-b lg:border-b-0 border-border" : i < arr.length - 1 ? "border-b md:border-b-0 border-border" : ""} hover:bg-obsidian hover:text-white transition-colors group cursor-default`}
                  data-testid={`what-we-do-tile-${s.key || i}`}
                >
                  <div className="text-[10px] font-mono tracking-[0.2em] text-execution mb-3 group-hover:text-execution">0{i + 1}</div>
                  <div className="font-heading text-sm lg:text-base font-semibold leading-tight">{s.title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Nexra Operates */}
      <section className="relative border-t border-border bg-muted overflow-hidden" data-testid="operates-section">
        <div className="absolute inset-0 bg-gridlines opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-12">
            <div className="lg:col-span-5">
              <div className="overline mb-4">02 — Operating model</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                {home.operates_title || "How Nexra Operates"}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4 flex">
              <p className="text-base lg:text-lg text-foreground/80 leading-relaxed max-w-2xl">
                {home.operates_intro}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 border border-border bg-white">
            {(home.operates_pillars || []).map((p, i) => (
              <div
                key={p.title}
                className={`p-6 lg:p-8 ${i !== 0 ? "lg:border-l border-border" : ""} ${i < (home.operates_pillars?.length || 0) - 1 ? "border-b lg:border-b-0 border-border" : ""}`}
                data-testid={`operates-pillar-${i}`}
              >
                <div className="text-[10px] font-mono tracking-[0.2em] text-execution mb-3">OP_0{i + 1}</div>
                <h3 className="font-heading text-xl font-semibold tracking-tight mb-3">{p.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {home.operates_outcomes && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="operates-outcomes">
              {home.operates_outcomes.map((o, i) => (
                <div key={o} className="flex items-center gap-3 border border-border bg-white px-4 py-3">
                  <span className="status-dot" />
                  <span className="text-sm font-medium">{o}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Evolution of Delivery */}
      <section className="border-t border-border bg-white" data-testid="evolution-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">03 — Evolution</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {home.evolution_title || "The Evolution of Delivery"}
            </h2>
            <p className="mt-6 font-heading text-lg lg:text-xl text-foreground/80 leading-snug max-w-md">
              {home.evolution_lead}
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base lg:text-lg text-foreground/80 leading-relaxed max-w-2xl">
              {home.evolution_body}
            </p>
            <p className="mt-6 text-sm lg:text-base text-foreground/70 leading-relaxed max-w-2xl">
              {home.evolution_combine_intro}
            </p>
            {home.evolution_outcomes && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="evolution-outcomes">
                {home.evolution_outcomes.map((o, i) => (
                  <div key={o} className="flex items-center gap-3 border border-border bg-muted px-4 py-3" data-testid={`evolution-outcome-${i}`}>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-execution">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm font-medium">{o}</span>
                  </div>
                ))}
              </div>
            )}
            {home.evolution_close && (
              <p className="mt-8 pt-6 border-t border-border text-sm lg:text-base text-foreground/70 leading-relaxed max-w-2xl italic">
                {home.evolution_close}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Two Models */}
      <section className="relative border-t border-border bg-obsidian text-white overflow-hidden" data-testid="models-section">
        <div className="absolute inset-0 bg-gridlines-dark opacity-40 pointer-events-none" />
        <div className="absolute inset-0 bg-radial-blue pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-16">
            <div className="lg:col-span-6">
              <div className="overline-light mb-4">04 — Two Models</div>
              <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                {home.models_title}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-4 flex items-end">
              <p className="text-base lg:text-lg text-white/70 max-w-lg">{home.models_intro}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 border border-white/15">
            {models.map((m, i) => (
              <div
                key={m.key}
                className={`p-8 lg:p-10 ${i === 1 ? "lg:border-l border-white/15" : ""} group hover:bg-white/[0.03] transition-colors`}
                data-testid={`model-card-${m.key}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="overline-light">0{i + 1}</div>
                  <span className={`chip-dark ${i === 0 ? "" : "bg-execution border-execution text-white"}`}>
                    {m.tag}
                  </span>
                </div>
                {i === 0 ? <Users className="h-6 w-6 text-execution mb-6" /> : <Target className="h-6 w-6 text-execution mb-6" />}
                <h3 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight mb-4">{m.title}</h3>
                <p className="text-sm lg:text-base text-white/70 leading-relaxed mb-6">{m.summary}</p>
                <ul className="space-y-2.5 mb-8">
                  {(m.bullets || []).map((b) => (
                    <li key={b} className="flex gap-3 text-sm text-white/80">
                      <span className="text-execution font-mono mt-0.5">→</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/service-models" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider border-b border-white/30 hover:border-execution hover:text-execution transition-colors pb-1">
                  Compare models
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI in Delivery */}
      <section className="section" data-testid="ai-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">05 — AI-Enabled</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {home.ai_title}
            </h2>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed max-w-md">{home.ai_body}</p>
            <Link to="/ai-delivery" className="mt-8 inline-flex items-center gap-2 text-sm font-medium border-b-2 border-execution pb-1 hover:gap-3 transition-all" data-testid="ai-section-link">
              <Sparkles className="h-4 w-4 text-execution" />
              See how AI runs alongside delivery
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 border border-border">
              {aiPillars.map((p, i) => {
                const Icon = AI_ICONS[i] || Sparkles;
                return (
                  <div
                    key={p.title}
                    className={`p-6 lg:p-8 ${i % 2 === 1 ? "md:border-l border-border" : ""} ${i < aiPillars.length - 2 ? "border-b border-border" : "md:border-b-0 border-b border-border last:border-b-0"} hover:bg-muted transition-colors`}
                    data-testid={`ai-pillar-${i}`}
                  >
                    <Icon className="h-5 w-5 text-execution mb-4" />
                    <div className="text-[10px] font-mono tracking-[0.2em] text-muted-foreground mb-2">AI_0{i + 1}</div>
                    <h3 className="font-heading text-lg lg:text-xl font-semibold tracking-tight mb-2">{p.title}</h3>
                    <p className="text-sm text-foreground/70 leading-relaxed">{p.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Future of Delivery — compact strategic band */}
      <section className="border-t border-border bg-muted" data-testid="future-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">06 — Future</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {home.future_title || "The Future of Delivery"}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base lg:text-lg text-foreground/80 leading-relaxed max-w-2xl">
              {home.future_body}
            </p>
            {home.future_pillars && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="future-pillars">
                {home.future_pillars.map((p, i) => (
                  <div key={p} className="border border-border bg-white p-5" data-testid={`future-pillar-${i}`}>
                    <div className="text-[10px] font-mono tracking-[0.2em] text-execution mb-3">F0{i + 1}</div>
                    <div className="font-heading text-base font-semibold leading-snug">{p}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* When to Engage */}
      <section className="border-t border-border bg-white" data-testid="when-engage-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">07 — Triggers</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {home.when_engage_title || "When to Engage Nexra"}
            </h2>
            <p className="mt-6 text-sm text-muted-foreground max-w-md">
              If any of these describe your program, Nexra should already be in the room.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border border-t border-b border-border">
              {(home.when_engage_items || []).map((item, i) => (
                <li key={i} className="py-6 flex gap-6 items-start group" data-testid={`engage-item-${i}`}>
                  <span className="font-mono text-xs text-execution tracking-widest w-10 shrink-0 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-lg lg:text-xl font-heading font-medium leading-snug group-hover:text-execution transition-colors duration-200">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/engage" data-testid="when-engage-cta" className="btn-primary">
                Engage Nexra
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/join-network" className="btn-secondary">Join Nexra Network</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA band */}
      <section className="relative border-t border-border bg-obsidian text-white overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-dotgrid-dark opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <h3 className="md:col-span-8 font-heading text-3xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
            People. AI. Delivery. Engineered together.
          </h3>
          <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 md:items-end md:justify-end">
            <Link to="/engage" className="btn-accent" data-testid="final-cta-engage">Engage Nexra</Link>
            <Link to="/join-network" className="btn-ghost-dark" data-testid="final-cta-network">Join Network</Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
